import { cn } from "@/lib/cn";
import { getBlurhashFromUrl } from "@/shared";
import Blurhash from "@components/blurhash";
import { useMemo, useState } from "react";
import type { TElement } from "../types";
import { Label } from "./Label";
import { Slider } from "./Slider";

const LABEL_STYLE =
  "after:tw-absolute after:tw-inset-0 after:tw-z-1 after:tw-size-full after:tw-[background:var(--overlay-color)] after:tw-opacity-[var(--overlay-opacity)] after:tw-content-['']";
export const Element = ({
  before,
  after,
  label,
  isActive,
  isVisible,
  shape,
}: TElement) => {
  const [value, setValue] = useState(50);
  const [isBeforeLoaded, setIsBeforeLoaded] = useState(false);
  const [isAfterLoaded, setIsAfterLoaded] = useState(false);

  const [prevIsActive, setPrevIsActive] = useState(isActive);
  if (isActive !== prevIsActive) {
    setPrevIsActive(isActive);
    if (!isActive) setValue(50);
  }

  const commonLabelProps = useMemo(
    () => ({
      labelTextColor: label.labelTextColor,
      labelBackgroundColor: label.labelBackgroundColor,
      labelBackgroundOpacity: label.labelBackgroundOpacity,
    }),
    [label],
  );

  const getLabelClassName = (position: "left" | "right") =>
    cn(`tw-max-w-[40%]`, {
      "tw-bottom-2 tw-top-auto": label.labelsPosition === "bottom",
      "tw-bottom-auto tw-top-1/2 -tw-translate-y-1/2":
        label.labelsPosition === "center" || shape === "circle",
      "tw-left-2 tw-right-auto tw-z-[4]": position === "left",
      "tw-right-2 tw-left-auto tw-z-[2]": position === "right",
    });

  const renderLabel = (side: "before" | "after") => {
    if (!label.showLabels) return null;
    const labelText = side === "before" ? before.label : after.label;
    if (!labelText) return null;
    return (
      <Label
        className={getLabelClassName(side === "before" ? "left" : "right")}
        {...commonLabelProps}
        label={labelText}
      />
    );
  };

  const beforeBlurhash = getBlurhashFromUrl(before.src);
  const afterBlurhash = getBlurhashFromUrl(after.src);

  return (
    <>
      <div
        style={
          {
            "--overlay-opacity": `${after.overlayOpacity || 0}%`,
            "--overlay-color": after.overlayColor || "#FFFFFF",
          } as React.CSSProperties
        }
        className={cn(
          "tw-absolute tw-left-0 tw-top-0 tw-size-full tw-overflow-hidden",
          {
            [LABEL_STYLE]: after.overlay,
          },
        )}
      >
        {isVisible && (
          <img
            src={after.src}
            alt={after.alt}
            className={cn("tw-size-full tw-object-cover", {
              "tw-grayscale": after.blackAndWhite,
            })}
            onLoad={() => setIsAfterLoaded(true)}
          />
        )}
        {afterBlurhash && !isAfterLoaded && (
          <Blurhash blurhash={afterBlurhash} />
        )}
        {renderLabel("after")}
      </div>
      <div
        className={cn(
          "tw-absolute tw-left-0 tw-top-0 tw-z-[3] tw-size-full tw-overflow-hidden",
          {
            [LABEL_STYLE]: before.overlay,
            "after:tw-z-[3]": before.overlay,
          },
        )}
        style={
          {
            clipPath: `inset(0 ${100 - value}% 0 0)`,
            "--overlay-opacity": `${before.overlayOpacity || 0}%`,
            "--overlay-color": before.overlayColor || "#FFFFFF",
          } as React.CSSProperties
        }
      >
        {isVisible && (
          <img
            src={before.src}
            alt={before.alt}
            className={cn("tw-size-full tw-object-cover", {
              "tw-grayscale": before.blackAndWhite,
            })}
            onLoad={() => setIsBeforeLoaded(true)}
          />
        )}
        {beforeBlurhash && !isBeforeLoaded && (
          <Blurhash blurhash={beforeBlurhash} />
        )}
        {renderLabel("before")}
      </div>
      <Slider value={value} setValue={setValue} />
    </>
  );
};
